// 45s count polling for the notification bell — single swap point for a future push transport.
import { useCallback, useEffect, useRef, useState } from "react";
import {
  getNotificationCountApi,
  listNotificationsApi,
  markNotificationDoneApi,
  markNotificationsSeenApi,
  type BrainNotification,
  type NotificationCount,
} from "./brain-api";

const POLL_MS = 45_000;

const EMPTY: NotificationCount = { unread: 0, unseen: 0, mentions: 0, replies: 0, reviews: 0, other: 0 };

export function useNotifications() {
  const [count, setCount] = useState<NotificationCount>(EMPTY);
  const [items, setItems] = useState<BrainNotification[]>([]);
  const [tab, setTab] = useState<"all" | "mentions" | "activity">("all");
  const [loading, setLoading] = useState(false);
  const [pulse, setPulse] = useState(false);
  const pulsedOnce = useRef(false);
  /** Bumped by openInbox so NotificationBell can open the popover from Today. */
  const [inboxOpenNonce, setInboxOpenNonce] = useState(0);

  const refreshCount = useCallback(async () => {
    try {
      const c = await getNotificationCountApi();
      setCount(c);
      if (c.unseen > 0 && !pulsedOnce.current) {
        pulsedOnce.current = true;
        setPulse(true);
        window.setTimeout(() => setPulse(false), 1800);
      }
    } catch {
      /* best-effort */
    }
  }, []);

  const refreshList = useCallback(async (t: "all" | "mentions" | "activity" = tab) => {
    setLoading(true);
    try {
      setItems(await listNotificationsApi(t));
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    void refreshCount();
    const id = window.setInterval(() => void refreshCount(), POLL_MS);
    const onVis = () => {
      if (document.visibilityState === "visible") void refreshCount();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [refreshCount]);

  const openInbox = useCallback(async () => {
    setInboxOpenNonce((n) => n + 1);
    await Promise.all([markNotificationsSeenApi().catch(() => null), refreshList(tab)]);
    await refreshCount();
  }, [refreshCount, refreshList, tab]);

  const changeTab = useCallback(async (t: "all" | "mentions" | "activity") => {
    setTab(t);
    await refreshList(t);
  }, [refreshList]);

  const markDone = useCallback(async (eventId: string) => {
    try {
      await markNotificationDoneApi(eventId);
      setItems((prev) => prev.map((n) => (n.eventId === eventId ? { ...n, doneAt: Date.now() } : n)));
      await refreshCount();
    } catch {
      /* ignore */
    }
  }, [refreshCount]);

  return {
    count,
    items,
    tab,
    loading,
    pulse,
    inboxOpenNonce,
    refreshCount,
    openInbox,
    changeTab,
    markDone,
  };
}
